import { useCallback, useRef, useEffect } from 'react';
import { apiClient } from '@/lib/apiClient';

export const useCallStatusPolling = () => {
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptRef = useRef(0);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        console.log('🧹 Cleaning up call status polling on unmount');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        pollAttemptRef.current = 0;
      }
    };
  }, []);

  const startStatusPolling = useCallback((callSid: string) => {
    if (!callSid) return;
    
    // Always stop any existing polling first to prevent memory leaks
    stopStatusPolling();
    
    pollAttemptRef.current = 0;
    console.log(`🔄 Starting status polling for call: ${callSid}`);
    
    const pollStatus = async () => {
      try {
        const data = await apiClient.get(`/twilio/call-status/${callSid}`);
        
        if (data.status && data.status !== 'unknown' && data.status !== 'initiating') {
          console.log(`📊 Call status update: ${data.status}`);
          pollAttemptRef.current = 0;
          
          // Stop polling if call has ended
          if (['completed', 'failed', 'busy', 'no-answer', 'cancelled'].includes(data.status)) {
            console.log(`📴 Call ended with status: ${data.status}, stopping polling`);
            stopStatusPolling();
            return;
          }
        } else {
          pollAttemptRef.current++;
          if (pollAttemptRef.current <= 3) {
            console.log(`⏳ Call status not yet available (attempt ${pollAttemptRef.current})`);
          }
        }
      } catch (error) {
        console.warn('⚠️ Error polling call status:', error);
      }
    };

    // Initial poll after short delay
    setTimeout(pollStatus, 1000);
    
    // Set up regular polling with longer interval (10 seconds instead of 5)
    pollIntervalRef.current = setInterval(pollStatus, 10000);
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      pollAttemptRef.current = 0;
      console.log('🛑 Stopped call status polling');
    }
  }, []);

  return {
    startStatusPolling,
    stopStatusPolling
  };
};